import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { ntxAPI } from '../../../API/axiosNeatex';

interface TaskDependency {
  id: string;
  taskId: string;
  dependsOnTaskId: string;
  dependencyType: string;
  dependsOnTaskName?: string;
  dependsOnTaskName2?: string;
}

// ==================== Async Thunks ====================

// 🔵 جلب التبعيات حسب رقم المهمة
export const getTaskDependenciesByTaskId = createAsyncThunk(
  'TaskDependency/getTaskDependenciesByTaskId',
  async (taskId: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.get(`Task/GetTaskDependencieByTaskId?taskId=${taskId}`);
      if (data?.succeeded) return data.data;
      return [];
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🟢 إضافة تبعية
export const addTaskDependency = createAsyncThunk(
  'TaskDependency/addTaskDependency',
  async (item: Omit<TaskDependency, 'id' | 'createdBy' | 'createdAt' | 'updatedAt'>, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/AddTaskDependencie', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في إضافة التبعية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ✏️ تعديل تبعية
export const updateTaskDependency = createAsyncThunk(
  'TaskDependency/updateTaskDependency',
  async (item: TaskDependency, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post('Task/UpdateTaskDependencie', item);
      if (data?.succeeded) return data.data;
      return rejectWithValue(data?.message || 'فشل في تحديث التبعية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// 🗑️ حذف تبعية
export const deleteTaskDependency = createAsyncThunk(
  'TaskDependency/deleteTaskDependency',
  async (id: string, { rejectWithValue }) => {
    try {
      const { data } = await ntxAPI.post(`Task/DeleteTaskDependency?Id=${id}`);
      if (data?.succeeded) return id;
      return rejectWithValue(data?.message || 'فشل في حذف التبعية');
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// ==================== Slice ====================

interface TaskDependencyState {
  taskDependencyList: TaskDependency[];
  loading: boolean;
  error: string | null;
}

const initialState: TaskDependencyState = {
  taskDependencyList: [],
  loading: false,
  error: null
};

const TaskDependencySlice = createSlice({
  name: 'TaskDependency',
  initialState,
  reducers: {
    clearTaskDependencies: state => {
      state.taskDependencyList = [];
      state.error = null;
      state.loading = false;
    },
    addLocalTaskDependency: (state, action: PayloadAction<TaskDependency>) => {
      state.taskDependencyList.unshift(action.payload);
    },
    updateLocalTaskDependency: (state, action: PayloadAction<TaskDependency>) => {
      const index = state.taskDependencyList.findIndex(d => d.id === action.payload.id);
      if (index !== -1) state.taskDependencyList[index] = action.payload;
    }
  },
  extraReducers: builder => {
    builder
      // 🔄 Get
      .addCase(getTaskDependenciesByTaskId.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getTaskDependenciesByTaskId.fulfilled, (state, action) => {
        state.loading = false;
        state.taskDependencyList = action.payload;
      })
      .addCase(getTaskDependenciesByTaskId.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // ➕ Add
      .addCase(addTaskDependency.fulfilled, (state, action) => {
        state.taskDependencyList.unshift(action.payload);
      })
      .addCase(addTaskDependency.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // ✏️ Update
      .addCase(updateTaskDependency.fulfilled, (state, action) => {
        const index = state.taskDependencyList.findIndex(d => d.id === action.payload.id);
        if (index !== -1) state.taskDependencyList[index] = action.payload;
      })
      .addCase(updateTaskDependency.rejected, (state, action) => {
        state.error = action.payload as string;
      })

      // 🗑️ Delete
      .addCase(deleteTaskDependency.fulfilled, (state, action) => {
        state.taskDependencyList = state.taskDependencyList.filter(d => d.id !== action.payload);
      })
      .addCase(deleteTaskDependency.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  }
});

export const { clearTaskDependencies, addLocalTaskDependency, updateLocalTaskDependency } = TaskDependencySlice.actions;

export default TaskDependencySlice.reducer;
